import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { TextField, Button, Box, Typography, LinearProgress, Alert } from '@mui/material';
import { createSubscription } from '../services/subscriptionService';
import { getAccountByGuid } from '../services/accountService';
import { getSubscriptionByGuid } from '../services/subscriptionService';

const Subscribe = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { accountGuid, selectedPlan, subscriptionGuid } = location.state || {};

    const [formValues, setFormValues] = useState({
        orgName: '',
        notes: '',
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [account, setAccount] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Fetch the account and subscription data
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (accountGuid) {
                    const accountData = await getAccountByGuid(accountGuid);
                    if (accountData) {
                        setAccount(accountData);
                        setFormValues((prev) => ({
                            ...prev,
                            orgName: accountData.accountName || accountData.contact || '',
                        }));
                        setSuccessMessage('Your account has been successfully created.');
                    } else {
                        throw new Error('Account not found');
                    }
                } else {
                    throw new Error('Missing account GUID');
                }

                if (subscriptionGuid) {
                    const subscriptionData = await getSubscriptionByGuid(subscriptionGuid);
                    if (subscriptionData) {
                        setSubscription(subscriptionData.subscription);
                    } else {
                        throw new Error('Subscription not found');
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                navigate('/error', { state: { message: error.message } });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [accountGuid, subscriptionGuid, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const subscriptionData = {
                subscriptionGuid: uuidv4(),
                accountGuid,
                orgName: formValues.orgName || `Organization ${Date.now()}`,
                notes: formValues.notes,
                planId: selectedPlan,
                isActive: 1,
                autoRenewStatus: true,
                subscriptionStatus: 'active',
            };

            const response = await createSubscription(subscriptionData);
            if (response && response.success) {
                navigate('/dashboard');
            } else {
                console.error('Failed to create subscription:', response.message);
            }
        } catch (error) {
            console.error('Error creating subscription:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <LinearProgress />;
    }

    return (
        <Box
            sx={{
                maxWidth: 500,
                margin: '0 auto',
                padding: 4,
                borderRadius: 2,
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            }}
        >
            {successMessage && (
                <Alert severity="success" sx={{ marginBottom: 2 }}>
                    {successMessage}
                </Alert>
            )}
            {subscription ? (
                <>
                    <Box
                        sx={{
                            border: '1px solid #ccc',
                            borderRadius: 2,
                            padding: 3,
                            backgroundColor: '#f9f9f9',
                            marginBottom: 2,
                        }}
                    >
                        <Typography variant="h5" component="h2" gutterBottom>
                            Subscription Details
                        </Typography>
                        <Typography variant="body1">
                            <strong>Plan:</strong> {subscription.planId}
                        </Typography>
                        <Typography variant="body1">
                            <strong>Organization Name:</strong> {subscription.orgName}
                        </Typography>
                        <Typography variant="body1">
                            <strong>Status:</strong> {subscription.subscriptionStatus}
                        </Typography>
                        <Typography variant="body1">
                            <strong>Auto Renew:</strong>{' '}
                            {subscription.autoRenewStatus ? 'Enabled' : 'Disabled'}
                        </Typography>
                        <Typography variant="body1">
                            <strong>Expires At:</strong>{' '}
                            {subscription.expiresAt ? new Date(subscription.expiresAt).toLocaleDateString() : 'Never expires'}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', marginTop: 4 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate('/dashboard')}
                        >
                            Continue to Dashboard
                        </Button>
                    </Box>
                </>
            ) : (
                <form onSubmit={handleSubmit}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Subscribe to {selectedPlan} Plan
                    </Typography>
                    <TextField
                        label="Organization Name"
                        name="orgName"
                        value={formValues.orgName}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        required
                    />
                    <TextField
                        label="Notes (Optional)"
                        name="notes"
                        value={formValues.notes}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        multiline
                        rows={4}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                        >
                            Subscribe
                        </Button>
                    </Box>
                </form>
            )}
        </Box>
    );
};

export default Subscribe;